import React, { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import {
  Route, Routes, useNavigate,
} from 'react-router-dom';
import { Box, Snackbar } from '@mui/material';

import Menu from './components/layout/Menu';
import ActivateUser from './components/ActivateUser';
import HelpDialog from './components/help/HelpDialog';
import Loader from './components/Loader';
import UserView from './views/UserView';
import AdminView from './views/admin/AdminView';
import UserManagementView from './views/admin/UserManagementView';
import InstituteManagement from './views/admin/InstituteManagementView';
import CertificatesView from './views/CertificatesView';
import TrialsView from './views/TrialsView';
import TrialDetailsView from './views/TrialDetailsView';
import LoginView from './views/LoginView';
import PasswordRequestView from './views/PasswordRequestView';
import PasswordResetView from './views/PasswordResetView';
import MySurveyView from './views/MySurveyView';
import EnrollmentsView from './views/EnrollmentsView';
import DownloadView from './views/DownloadView';
import PaymentSuccessView from './views/PaymentSuccessView';
import PayoneMock from './views/PayoneMock';
import ShowSubmissionView from './views/ShowSubmissionView';
import GroundTruthView from './views/GroundTruthView';
import RegisterView from './views/RegisterView';
import FeedbackView from './views/FeedbackView';
import ReviewPublicSurveyView from './views/ReviewPublicSurveyView';
import ReviewTrialsView from './views/ReviewTrialsView';
import ReviewSubmissionView from './views/ReviewSubmissionView';
import ReviewManagementView from './views/ReviewManagementView';
import CreateEditSurveyView from './views/CreateEditSurveyView';
import RedeemVoucherView from './views/RedeemVoucherView';
import VoucherEnrollView from './views/VoucherEnrollView';
import VoucherManagementView from './views/admin/VoucherManagementView';
import InstituteUsersView from './views/admin/InstituteUsersView';
import DigitalSurveyEditor from './views/admin/DigitalSurveyEditor';
import DigitalSurveyMenuView from './views/DRTViews/DigitalSurveyMenuView';
import AntibodySelectionView from './views/DRTViews/AntibodySelectionView';
import DigitalQuestionnaireView from './views/DRTViews/DigitalQuestionnaireView';
import SurveyView from './views/SurveyView';
import ReviewView from './views/ReviewView';
import { RootState } from './redux/types';

import {
  hideHelp,
  hideNotification,
  fetchTrialCategories,
  fetchReviewCategories,
} from './redux/actions';

import { DEVMODE } from './environment';
import SurveyCopyView from './views/admin/SurveyCopyView';
import LoopbackProvider from './loopback/LoopbackProvider';
import AuswertungSettings from './views/admin/AuswertungSettings';
import EvaluationView from './views/EvaluationView';
import SurveyEditor from './views/admin/SurveyEditor';

const mapStateToProps = (state: RootState) => ({
  notifications: state.app.notifications,
  showHelp: state.app.help.show,
  helpId: state.app.help.id,
  isLoading: state.app.isLoading,
  loadingText: state.app.loadingText,
  isLoggedIn: !!state.users.token,
});

const dispatchProps = {
  fetchTrialCategories,
  fetchReviewCategories,
  hideNotification,
  hideHelp,
};

const connector = connect(mapStateToProps, dispatchProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux;

const App: React.FC<Props> = ({
  showHelp,
  helpId,
  notifications,
  isLoading,
  loadingText,
  isLoggedIn,
  hideHelp: _hideHelp,
  hideNotification: _hideNotification,
  fetchTrialCategories: fetchTrialCategoriesAction,
  fetchReviewCategories: fetchReviewCategoriesAction,
}: Props) => {
  const navigate = useNavigate();
  useEffect(() => {
    fetchTrialCategoriesAction();
    fetchReviewCategoriesAction();
  }, [fetchTrialCategoriesAction, fetchReviewCategoriesAction]);

  useEffect(() => {
    LoopbackProvider.navigate = navigate;
  }, [navigate]);

  const maxWidth = isLoggedIn ? 'calc(100% - 251px)' : '100%'; // 1px extra because menu has border;
  return (
    <div className="z-1">
      <Loader isLoading={isLoading} text={loadingText} />
      <div className="flex">
        {isLoggedIn && (<Menu />)}
        <Box sx={{
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          flex: 1,
          maxWidth,
        }}
        >
          <Routes>
            <Route path="/" element={<LoginView />} />
            <Route path="/user" element={<UserView />} />
            <Route path="/usermanagement" element={<UserManagementView />} />
            <Route path="/institutemanagement" element={<InstituteManagement />} />
            <Route path="/institutemanagement/:instituteId/users" element={<InstituteUsersView />} />
            <Route path="/admin" element={<AdminView />} />
            <Route path="/trials" element={<TrialsView />} />
            <Route path="/trialdetail/:trialId" element={<TrialDetailsView />} />
            <Route path="/trialdetail/:trialId/enrollment/:enrollmentId/evaluation" element={<EvaluationView />} />
            <Route path="/createeditsurvey/:trialId" element={<CreateEditSurveyView />} />
            <Route path="/register" element={<RegisterView />} />
            <Route path="/login" element={<LoginView />} />
            <Route path="/passwordRequest" element={<PasswordRequestView />} />
            <Route path="/passwordReset" element={<PasswordResetView />} />
            <Route path="/survey/:trialId" element={<MySurveyView />} />
            <Route path="/survey/:trialId/:enrollmentId" element={<MySurveyView />} />
            <Route path="/enrollments" element={<EnrollmentsView />} />
            <Route path="/certificates" element={<CertificatesView />} />
            <Route path="/groundTruth/:trialId" element={<GroundTruthView />} />
            <Route path="/activate" element={<ActivateUser />} />
            <Route path="/feedback/:trialId" element={<FeedbackView />} />
            <Route path="/paymentSuccess" element={<PaymentSuccessView />} />
            <Route path="/download/:containerName/:fileName" element={<DownloadView />} />
            <Route path="/download" element={<DownloadView />} />
            <Route path="/showSubmission/:enrollmentId" element={<ShowSubmissionView />} />
            <Route path="/pblc" element={<ReviewPublicSurveyView />} />
            <Route path="/reviews" element={<ReviewTrialsView />} />
            <Route path="/review" element={<ReviewSubmissionView />} />
            <Route path="/reviewmanagement" element={<ReviewManagementView />} />
            <Route path="/redeem" element={<RedeemVoucherView />} />
            <Route path="/voucherenroll" element={<VoucherEnrollView />} />
            <Route path="/vouchermanagement/:trialId" element={<VoucherManagementView />} />
            <Route path="/digitaltrial/:trialId/digitalsurvey/editor" element={<DigitalSurveyEditor />} />
            <Route path="/digitaltrial/:trialId/menu" element={<DigitalSurveyMenuView />} />
            <Route path="/digitaltrial/:trialId/survey/:surveyId/antibodies" element={<AntibodySelectionView />} />
            <Route path="/digitaltrial/:trialId/survey/:surveyId/antibody/:antibodyId/form" element={<DigitalQuestionnaireView />} />
            <Route path="/trial/:trialId/survey/editor" element={<SurveyEditor />} />
            <Route path="/trial/:trialId/survey/:surveyId/editor/copy" element={<SurveyCopyView />} />
            <Route path="/trial/:trialId/survey/:surveyId/section/:sectionId/editor/copy" element={<SurveyCopyView isQuestionCopier />} />
            <Route path="/trial/:trialId/survey" element={<SurveyView />} />
            <Route path="/trial/:trialId/enrollment/:enrollmentId/review" element={<ReviewView />} />
            <Route path="/showDigitalSubmission/trial/:trialId/enrollment/:enrollmentId" element={<SurveyView showEnrollmentsSubmissions />} />
            <Route path="/trial/:trialId/auswertungsettings" element={<AuswertungSettings />} />
            {DEVMODE && <Route path="/payoneMock" element={<PayoneMock />} />}
          </Routes>
        </Box>
      </div>
      {
        notifications.queue.length > 0
        && (
          <Snackbar
            sx={notifications.queue[notifications.queue.length - 1].type === 'error' ? {
              '& div': {
                backgroundColor: 'error.dark',
              },
            } : null}
            open={notifications.queue.length > 0}
            message={notifications.queue[notifications.queue.length - 1].text}
            autoHideDuration={10000}
            onClose={_hideNotification}
          />
        )
      }
      <HelpDialog
        isOpen={showHelp}
        id={helpId}
        onClose={_hideHelp}
      />
    </div>
  );
};

export default connector(App);
